import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Rate } from '@spike/boarding-rule-model';
import { RootState } from 'store';
import { showErrorThunk } from '@spike/notifications-action';
import { OverFullWindow } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import { RateRuleNameSection } from './Sections/RateRuleNameSection/RateRuleNameSection';
import { SelectDatesSection } from './Sections/SelectDatesSection/SelectDatesSection';
import { ServicesSection, rateRulesConfig } from 'components/Boarding/Components/ServicesSection';
import { OccupancyRulesSection } from './Sections/OccupancyRulesSection/OccupancyRulesSection';
import { RateRuleFormState } from './RateRuleFormState';
import { ValidationErrors, OccupancyRule } from './types';
import { useBoardingRateRules } from '../BoardingRateRulesProvider';
import styles from './RateRuleForm.module.scss';

interface RateRuleFormProps {
    onClose: () => void;
    rateRule?: Rate;
}

const RateRuleForm: FunctionComponent<RateRuleFormProps> = ({ onClose, rateRule }) => {
    const dispatch = useDispatch();
    const { saveRateRule, boardingServices, marketplaceId, isSaving } = useBoardingRateRules();
    const allRateRules = useSelector((state: RootState) => state.rateRules.rates);

    const [formState, setFormState] = useState<RateRuleFormState>(() =>
        rateRule ? RateRuleFormState.fromRateRule(rateRule, boardingServices) : RateRuleFormState.createEmpty()
    );

    const [errors, setErrors] = useState<ValidationErrors>({});
    const [showExitPrompt, setShowExitPrompt] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [serverConflictingRuleName, setServerConflictingRuleName] = useState<string | null>(null);
    const [serverAlwaysOnConflictService, setServerAlwaysOnConflictService] = useState<string | null>(null);

    const clientConflictingRuleName = useMemo((): string | null => {
        if (!formState.datesEnabled || !formState.startDate || !formState.endDate) return null;
        if (formState.services.length === 0) return null;

        const selectedServiceIds = new Set(formState.services.map(s => s.serviceId));
        const formStart = formState.startDate.getTime();
        const formEnd = formState.endDate.getTime();

        const conflicting = allRateRules.find(rule => {
            if (!rule.active) return false;
            if (rule.id === formState.originalId) return false;
            if (!rule.startDate?.isValid() || !rule.endDate?.isValid()) return false;
            const ruleStart = rule.startDate.valueOf();
            const ruleEnd = rule.endDate.valueOf();
            if (ruleStart > formEnd || ruleEnd < formStart) return false;
            return (rule.rateItems ?? []).some(item => selectedServiceIds.has(item.itemId));
        });

        return conflicting?.name ?? null;
    }, [
        allRateRules,
        formState.datesEnabled,
        formState.startDate,
        formState.endDate,
        formState.services,
        formState.originalId
    ]);

    const conflictingRuleName = clientConflictingRuleName ?? serverConflictingRuleName;

    const showAlwaysOnWarning = useMemo((): boolean => {
        if (!formState.datesEnabled || !formState.startDate || !formState.endDate) return false;
        if (formState.services.length === 0) return false;

        const selectedServiceIds = new Set(formState.services.map(s => s.serviceId));

        return allRateRules.some(rule => {
            if (!rule.active) return false;
            if (rule.id === formState.originalId) return false;
            if (rule.startDate?.isValid() || rule.endDate?.isValid()) return false;
            return (rule.rateItems ?? []).some(item => selectedServiceIds.has(item.itemId));
        });
    }, [
        allRateRules,
        formState.datesEnabled,
        formState.startDate,
        formState.endDate,
        formState.services,
        formState.originalId
    ]);

    const getTitle = () => {
        return formState.isEditMode ? 'Edit Rate Rule' : 'Add Rate Rule';
    };

    const handleNameChange = useCallback((name: string) => {
        setFormState(prev => prev.with({ name }));
        setErrors(prev => ({ ...prev, name: undefined }));
    }, []);

    const handleDatesChange = useCallback(
        (data: { enabled: boolean; startDate: Date | null; endDate: Date | null }) => {
            setFormState(prev =>
                prev.with({
                    datesEnabled: data.enabled,
                    startDate: data.startDate,
                    endDate: data.endDate
                })
            );
            setErrors(prev => ({ ...prev, startDate: undefined, endDate: undefined }));
            setServerConflictingRuleName(null);
            setServerAlwaysOnConflictService(null);
        },
        []
    );

    const handleServicesChange = useCallback((services: typeof formState.services) => {
        setFormState(prev => prev.with({ services }));
        setErrors(prev => ({ ...prev, services: undefined }));
        setServerConflictingRuleName(null);
        setServerAlwaysOnConflictService(null);
    }, []);

    const handleOccupancyRulesChange = useCallback((occupancyRules: OccupancyRule[]) => {
        setFormState(prev => prev.with({ occupancyRules }));
        setErrors(prev => ({ ...prev, occupancyRules: undefined }));
    }, []);

    const isFormValid = useMemo((): boolean => {
        if (!formState.name.trim()) return false;

        if (formState.datesEnabled) {
            if (!formState.startDate || !formState.endDate) return false;
            if (formState.endDate < formState.startDate) return false;
        }

        if (formState.services.length === 0) return false;
        if (formState.occupancyRules.length === 0) return false;

        const allOccupancyRulesComplete = formState.occupancyRules.every(
            rule => rule.operator && rule.percentage && rule.action && rule.amount && rule.currency
        );
        if (!allOccupancyRulesComplete) return false;

        return true;
    }, [formState]);

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {};

        if (!formState.name.trim()) {
            newErrors.name = 'Rate rule name is required';
        }

        if (formState.datesEnabled) {
            if (!formState.startDate) {
                newErrors.startDate = 'Start date is required';
            }

            if (!formState.endDate) {
                newErrors.endDate = 'End date is required';
            }

            if (formState.startDate && formState.endDate && formState.endDate < formState.startDate) {
                newErrors.endDate = 'End date must be after start date';
            }
        }

        if (formState.services.length === 0) {
            newErrors.services = 'At least one service is required';
        }

        if (formState.occupancyRules.length === 0) {
            newErrors.occupancyRules = 'At least one occupancy rule is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = useCallback(async () => {
        if (!validateForm()) {
            return;
        }

        try {
            await saveRateRule(formState.toSaveRateRule(marketplaceId));
            setIsSuccess(true);
        } catch (err: unknown) {
            const responseData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
            if (responseData && typeof responseData === 'object') {
                if (Array.isArray(responseData.base)) {
                    const message = (responseData.base as string[])[0] ?? '';
                    const alwaysOnMatch = message.match(/An Always On rate rule already exists.+:\s*(.+)/i);
                    if (alwaysOnMatch) {
                        setServerAlwaysOnConflictService(alwaysOnMatch[1].trim());
                    } else {
                        const overlapMatch = message.match(/overlaps with another rate rule:\s*(.+)/i);
                        setServerConflictingRuleName(overlapMatch?.[1]?.trim() ?? 'the conflicting rule');
                    }
                } else if (Array.isArray(responseData.name)) {
                    dispatch(showErrorThunk(`Rate rule name ${(responseData.name as string[])[0]}`));
                }
            }
        }
    }, [formState, marketplaceId, saveRateRule, dispatch]);

    const handleClose = useCallback(() => {
        setShowExitPrompt(true);
    }, []);

    const getSuccessMessage = () => {
        return formState.isEditMode ? (
            <span>
                Rate rule <strong>{formState.name}</strong> has been successfully updated.
            </span>
        ) : (
            <span>
                Rate rule <strong>{formState.name}</strong> has been created and is now active.
            </span>
        );
    };

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={getTitle()}
                onClose={onClose}
            >
                <SuccessScreen
                    title="Good Job"
                    message={getSuccessMessage()}
                    buttonLabel="Return to Rate Rules"
                    onClick={onClose}
                    className={styles.successScreen}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={getTitle()}
            onClose={handleClose}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={onClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            <div className={styles.container}>
                <form
                    className={styles.form}
                    onSubmit={e => e.preventDefault()}
                >
                    <RateRuleNameSection
                        value={formState.name}
                        onChange={handleNameChange}
                        error={errors.name}
                    />

                    <SelectDatesSection
                        enabled={formState.datesEnabled}
                        startDate={formState.startDate}
                        endDate={formState.endDate}
                        onChange={handleDatesChange}
                        errors={errors}
                    />
                    {formState.active && conflictingRuleName && (
                        <AlertMessage
                            icon={faTriangleExclamation}
                            bgColor="#F5F0E8"
                            bdColor="#D4C5A9"
                            iconColor="#C27803"
                            className={styles.saveError}
                        >
                            {'Another rule is set for the same time period. Please update '}
                            <strong>{conflictingRuleName}</strong>
                            {' to avoid conflicts.'}
                        </AlertMessage>
                    )}

                    {serverAlwaysOnConflictService && (
                        <AlertMessage
                            icon={faTriangleExclamation}
                            bgColor="#F5F0E8"
                            bdColor="#D4C5A9"
                            iconColor="#C27803"
                            className={styles.saveError}
                        >
                            {'An "Always On" rate rule already exists for the selected service(s): '}
                            <strong>{serverAlwaysOnConflictService}.</strong>
                            {' Please update services and/or dates to avoid conflicts'}
                        </AlertMessage>
                    )}

                    {formState.active && !conflictingRuleName && showAlwaysOnWarning && (
                        <AlertMessage
                            icon={faTriangleExclamation}
                            bgColor="#F5F0E8"
                            bdColor="#D4C5A9"
                            iconColor="#C27803"
                            className={styles.saveError}
                        >
                            {
                                "There's another rule that's always on for these services. This new rule will take priority during the selected dates."
                            }
                        </AlertMessage>
                    )}

                    <ServicesSection
                        services={formState.services}
                        availableServices={boardingServices}
                        onChange={handleServicesChange}
                        error={errors.services}
                        config={rateRulesConfig}
                    />

                    <OccupancyRulesSection
                        rules={formState.occupancyRules}
                        onChange={handleOccupancyRulesChange}
                        error={errors.occupancyRules}
                    />

                    <div className={styles.actions}>
                        <Button
                            label="Save"
                            variant="primary"
                            size="large"
                            onClick={handleSave}
                            disabled={
                                !isFormValid ||
                                (formState.active && !!conflictingRuleName) ||
                                !!serverAlwaysOnConflictService
                            }
                            isLoading={isSaving}
                            noColorOnDisabled={true}
                        />
                    </div>
                </form>
            </div>
        </OverFullWindow>
    );
};

export default RateRuleForm;
